import { flatMap } from 'lodash';
import {
  ClickFacet,
  ClicksBySelector,
  ElemStats,
  MatchingElement,
  Stat,
  VisibilityInfo,
} from './activitymaps';
import { int64 } from '@fullstory/proto/google/protobuf';
import { parseSelector, SelectorForElem, ToSelectorParts } from '@fullstory/selectors';
import { Q } from '@fullstory/tomato';
import { getPercentString } from '@fullstory/util/fmt';
import {
  Area,
  AreaRatio,
  AreasMoreSimilarThan,
  ClientRectIntersection,
  ElemBounds,
  RectIntersections,
  ViewportRect,
} from '../overlays/rectangles';
import { Point } from '@fullstory/util/geometry';
import { Matrix } from '@fullstory/util/matrix';
import { intl } from '@fullstory/intl';
import { Renderer, PageContext, GroupByPropertyDisplay } from '@fullstory/renderer-shared';
import { ElementFilteredDetails } from './types';
import { SelectorChain, SelectorPart } from '@fullstory/proto/fs/selectors';
import { elemIdent } from './ident';
import {
  PropertyValuesConfig,
  computePropertyValuesConfig,
  computeFocusPaths,
  satisfiesConstraints,
} from './properties';
import { RenderedNode } from '@fullstory/playback-web';
import { SimpleRect } from '@fullstory/util/rect';

const BIG_RECT_SIDE_THRESH = 0.75; // % of the viewport in a single dimension that would cause us to elide a box.
const BIG_RECT_THRESH = 0.5; // % of viewport for us to elide a rectangle.
const MERGING_AREA_SIMILARITY_THRESH = 0.45; // Only merge rectangles if their area ratio closer to 1 (1 being equal) than 0.4.
const MERGING_CLICK_COUNT_THRESH = 0.05; // Don't merge boxes that have very disparate click counts.
const NEGATIVE_HEIGHT_THRESH = 0.3; // Only examine negative spaces if the inner element is tall enough.
const NEGATIVE_AREA_SIMILARITY_THRESH = 0.1; // Merge if the horizontal negative space has an area ratio < 0.1.

export interface ElementQueryMatch {
  frameAncestry: HTMLIFrameElement[];
  elements: Q.OfMany;
}

export interface ElementInfoQueryMatch {
  selector: SelectorChain;
  rect: DOMRect;
}

// ClickStats capture the click statistics for a matching DOM element.
// This makes it the WEB-SPECIFIC version of a MatchingElement.
export class ClickStats implements MatchingElement {
  queryMatches: ElementQueryMatch[];

  private _stats: ElemStats;

  constructor(
    protected _facets: ClickFacet,
    private _pivotNoun: string,
    public clicks: ClicksBySelector,
    public inspectMode?: boolean,
  ) {
    this._stats = {
      NamedElementId: clicks.namedElementId,
      Parsed: clicks.selector.parsed,
      Selector: clicks.selector.raw,
      HumanReadableSelector: clicks.selector.humanReadable,
      PropertyValue: clicks.propertyValue,
      Type: this._facets.type,
      NumClicks: this.clicks.count,
      NumUsers: this.clicks.users,
      NumElements: 0,
      Stats: this._renderStats(inspectMode),
      Footnote: '',
      VisibilityInfo: <VisibilityInfo>{ ExistsInDom: false },
    };
  }

  pivot(): string {
    return this._pivotNoun;
  }

  selector(): string {
    return this.stats().Selector;
  }

  propertyValue(): string | undefined {
    return this.stats().PropertyValue;
  }

  identKey(): string {
    return elemIdent(this.selector(), this.propertyValue());
  }

  isNamedElement(): boolean {
    return !!this._stats.NamedElementId;
  }

  humanReadableSelector(): string {
    return this.stats().HumanReadableSelector;
  }

  stats(): ElemStats {
    return this._stats;
  }

  bindTo(wnd: Window, queryMatches: ElementQueryMatch[], allowHighlightRelatedElements: boolean) {
    if (!queryMatches || queryMatches.length == 0) {
      console.error('bindTo called with zero matches');
      return;
    }

    this.queryMatches = queryMatches;
    const topMostMatch = this.topMostMatch();

    // Set this to something, even if it's hidden
    this._stats.VisibilityInfo = extractVisibilityInfo(wnd, topMostMatch.elements.oneAt(0));

    // TODO(jaime|dutton): Consider doing this for each of the matches. For now
    //  just continue to do the first visible one.
    for (let index = 0; index < topMostMatch.elements.size(); index++) {
      const visibilityInfo = extractVisibilityInfo(wnd, topMostMatch.elements.oneAt(index));
      if (visibilityInfo.CouldBeClicked) {
        this._stats.VisibilityInfo = visibilityInfo;
        this._stats.NumElements = topMostMatch.elements.size();
        if (this._stats.NumElements > 1) {
          if (allowHighlightRelatedElements) {
            this._stats.Footnote = `${this._stats.NumElements} possible elements`;
          } else {
            this._stats.Footnote = `(${this._stats.NumElements} possible elements)`;
          }
        }

        // Adjust the "top match" so things work a little nicer down stream if the first item isn't visible
        if (index !== 0) {
          topMostMatch.elements.e = [
            topMostMatch.elements.e[index],
            ...topMostMatch.elements.e.slice(0, index),
            ...topMostMatch.elements.e.slice(index + 1, topMostMatch.elements.e.length),
          ];
        }
        break;
      }
    }
  }

  setInTheShadow(inTheShadow: boolean) {
    this._stats.VisibilityInfo.ExistsInTheShadow = inTheShadow;
  }

  // Returns true if any of the matching elements are a leaf node
  isLeaf(): boolean {
    // like some of the other functions here, this is relying on the "top match" and first element.
    const queryMatch = this.topMostMatch();

    if (!queryMatch) {
      return false;
    }

    for (let i = 0; i < queryMatch.elements.size(); i++) {
      const e = queryMatch.elements.oneAt(i);
      if (e.children().size() == 0) {
        return true; // return true if any of the elements are a leaf
      }
    }

    return false;
  }

  // The selector query can return many matches across multiple documents.
  // Choose the first one as a representative for the group. Hopefully it's
  // the shallowest / first-est node.
  topMostMatch(): ElementQueryMatch {
    if (!this.queryMatches) {
      return null;
    }

    return this.queryMatches[0];
  }

  r(): SimpleRect {
    const queryMatch = this.topMostMatch();

    if (!queryMatch) {
      return null;
    }

    // Arbitrarily choose the first element to represent the bounding box for the group
    const element = queryMatch.elements.oneAt(0);
    return this.offsetBounds(element, queryMatch.frameAncestry);
  }

  allRects(): SimpleRect[] {
    // For the given SelectorQueryMatch, compute the bounding rect for each of
    // its elements, including an offset for any parent iframes
    const computeOffsetBounds = (queryMatch: ElementQueryMatch): SimpleRect[] => {
      const bounds = [];
      queryMatch.elements.eachQ(x => bounds.push(this.offsetBounds(x, queryMatch.frameAncestry)));

      return bounds;
    };

    return flatMap(this.queryMatches, x => computeOffsetBounds(x));
  }

  private offsetBounds(element: Q.OfOne, frameAncestry: HTMLIFrameElement[]): SimpleRect {
    if (frameAncestry.length === 0) {
      return element.bounds();
    }

    const bounds = element.bounds();
    const offset = { left: 0, top: 0 };

    // The element bounds are relative to any parent iframes
    frameAncestry.forEach(frame => {
      if (!frame.contentWindow || !frame.contentWindow.frameElement) {
        return;
      }

      const bounds = frame.contentWindow.frameElement.getBoundingClientRect();

      offset.left += bounds.left;
      offset.top += bounds.top;
    });

    return {
      top: bounds.top + offset.top,
      bottom: bounds.bottom + offset.top,
      left: bounds.left + offset.left,
      right: bounds.right + offset.left,
      width: bounds.width,
      height: bounds.height,
    } as SimpleRect;
  }

  mergeClicks(clicks: ClicksBySelector) {
    // Merge (add) the click counts. For user count use the max value
    // Otherwise we would double count the users in some cases

    // TODO(aditya|jaime|dutton) for correctly merging the user counts we need
    // |set(users in From) union set(users in Into) - set(set(users in From) intersection (users in Into))|
    this.clicks.count += clicks.count;
    this.clicks.users = Math.max(this.clicks.users, clicks.users);
    this._stats.NumClicks = this.clicks.count;
    this._stats.NumUsers = this.clicks.users;
    this._stats.Stats = this._renderStats();
  }

  /**
   *
   * @param inspectMode A hack to modify the resulting Val strings. These Stats should rather be numbers, and the caller can present them how they want.
   *   Since inspect mode utilizes a single metric, there is no percentage based data to show.
   * @returns A 2-tuple of Stat
   * TODO(jb): Update the Stat interface to just be numbers instead of strings.
   */
  _renderStats(inspectMode?: boolean): [Stat, Stat] {
    const clicks = this.clicks;
    const facets = this._facets;

    const primary = {
      Name: intl.formatMessage(
        {
          defaultMessage:
            'Total {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}}',
          id: 'j95pjb',
        },
        {
          clickType: facets.type,
        },
      ),
      Val: inspectMode
        ? intl.formatMessage(
            {
              defaultMessage:
                '{clickCount, number} {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}}',
              id: 'FDhrOu',
            },
            {
              clickCount: clicks.count,
              clickType: facets.type,
            },
          )
        : intl.formatMessage(
            {
              defaultMessage:
                '{clickCount, number} {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}} \u2219 {percentage}',
              id: 'f9VAnF',
            },
            {
              clickCount: clicks.count,
              clickType: facets.type,
              percentage: getPercentString(clicks.count, facets.totalClicks),
            },
          ),
      InfoText: intl.formatMessage(
        {
          defaultMessage:
            '{clickCount, number} ({percentage}) of {clickTotal, number} {clickType, select, 2 {dead clicks} 3 {error clicks} 4 {rage clicks} other {clicks}} on this Page',
          id: '/GLSBL',
        },
        {
          clickCount: clicks.count,
          clickTotal: facets.totalClicks,
          percentage: getPercentString(clicks.count, facets.totalClicks),
          clickType: facets.type,
        },
      ),
    };

    const secondary = {
      Name: intl.formatMessage(
        {
          defaultMessage:
            'Users {clickType, select, 2 {dead clicked} 3 {error clicked} 4 {rage clicked} other {clicked}}',
          id: 'LXknWv',
        },
        {
          clickType: facets.type,
        },
      ),
      Val: inspectMode
        ? intl.formatMessage(
            {
              defaultMessage:
                '{userCount, number} {clickType, select, 2 {dead clicked} 3 {error clicked} 4 {rage clicked} other {clicked}}',
              id: 'uOz1tw',
            },
            {
              userCount: clicks.users,
              clickType: facets.type,
            },
          )
        : intl.formatMessage(
            {
              defaultMessage:
                '{userCount, number} {clickType, select, 2 {dead clicked} 3 {error clicked} 4 {rage clicked} other {clicked}} \u2219 {percentage}',
              id: 'DVcUWr',
            },
            {
              userCount: clicks.users,
              clickType: facets.type,
              percentage: getPercentString(clicks.users, facets.totalUsers),
            },
          ),
      InfoText: intl.formatMessage(
        {
          defaultMessage:
            '{userCount, number} ({percentage}) of {userTotal, number} users who clicked on this Page',
          id: 'o+vwvI',
        },
        {
          userCount: clicks.users,
          userTotal: facets.totalUsers,
          percentage: getPercentString(clicks.users, facets.totalUsers),
        },
      ),
    };

    return [primary, secondary];
  }
}

// Convert an ElementFacetResponse into an array of ElemStats by measuring what is visible in the DOM,
// doing bounding box collapsing and merging, and other nasty things.
export function createElemAndStats({
  wnd,
  doc,
  renderer,
  facet,
  allowHighlightRelatedElementsEnabled,
  pageContext,
  focusSelectors,
  groupByProperty,
}: {
  wnd: Window;
  doc: Document;
  renderer: Renderer;
  facet: ClickFacet;
  allowHighlightRelatedElementsEnabled: boolean;
  pageContext: PageContext;
  focusSelectors: string[];
  groupByProperty: GroupByPropertyDisplay | undefined;
}): { visibleClickElems: ClickStats[]; filteredElementDetails: ElementFilteredDetails[] } {
  const clicksAndStats = facet.clicks.map(x => new ClickStats(facet, 'user', x));
  const deduped = dedupe(clicksAndStats);
  const gathered = gather(
    wnd,
    doc,
    renderer,
    deduped,
    allowHighlightRelatedElementsEnabled,
    focusSelectors,
    groupByProperty,
  );

  const { mergedClickElems, filteredElementDetails } = merge({
    wnd,
    topElemsArg: gathered,
    pageContext,
    pageDefId: facet.pageDefId,
  });

  // TODO(jaime, stephanie): deal with stable lists. And toggle to show things that are present in the DOM.
  // For now, filter out anything that is either not in the DOM, or not visible.
  const visibleClickElems = mergedClickElems.filter((e, i) => {
    const allowThrough =
      e.stats().VisibilityInfo.ExistsInDom && e.stats().VisibilityInfo.CouldBeClicked;
    if (!allowThrough) {
      // TODO(jaime): Debug why we get SOOOooo many elements here that aren't on screen. We get elements seemingly from other pages.
      // Introspect clustering to see what's up.
      // console.log("Omitting element not on the current screen: ", e);
      if (e.stats().VisibilityInfo.ExistsInTheShadow) {
        filteredElementDetails.push({
          inTheShadow: true,
          elementDetails: { isNamedElement: e.isNamedElement(), selector: e.selector() },
          pageArgs: {
            orgId: pageContext.orgId,
            userId: pageContext.userId as int64,
            sessionId: pageContext.sessionId as int64,
            pageId: pageContext.pageId as int64,
          },
          pageDefId: 0,
        });
      }
    }
    return allowThrough;
  });

  // Resort descending. Merging of boxes could change the sort order.
  visibleClickElems.sort((a, b) => defaultClickBySelectorSort(a.clicks, b.clicks));

  return { visibleClickElems, filteredElementDetails };
}

/**
 * Sorts clicks stats descending by: Clicks -> Users -> Raw Selector
 */
export function defaultClickBySelectorSort(a: ClicksBySelector, b: ClicksBySelector) {
  // Sort first and foremost on the primary count (eg. clicks).
  const primaryStat = b.count - a.count;
  if (primaryStat != 0) {
    return primaryStat;
  }

  // If we have a secondary statistic (eg. Users). Use it as the secondary sort order.
  if (a.users) {
    const secondaryStat = b.users - a.users;
    if (secondaryStat != 0) {
      return secondaryStat;
    }
  }

  // Fallback to alpha sort.
  if (b.selector.raw > a.selector.raw) {
    return 1;
  } else if (b.selector.raw < a.selector.raw) {
    return -1;
  }

  // Fallback to alpha sort of property value.
  // Prefer global result first though.
  if (a.propertyValue !== undefined && b.propertyValue !== undefined) {
    return b.propertyValue > a.propertyValue ? 1 : -1;
  } else if (b.propertyValue !== undefined) {
    return -1;
  } else if (a.propertyValue !== undefined) {
    return 1;
  }

  return 0; // Entirely the same.
}

// Since we did selector canonicalization. We ought to dedupe any selectors whose string canonicalized to the same string.
function dedupe(stats: ClickStats[]): ClickStats[] {
  const deduped: { [key: string]: ClickStats } = {};
  for (let i = 0; i < stats.length; i++) {
    const stat = stats[i];
    const key = stat.identKey();
    const existing = deduped[key];
    if (existing) {
      existing.mergeClicks(stat.clicks);
    } else {
      deduped[key] = stat;
    }
  }

  // Iteration order is insertion order.
  const rtn = [];
  for (const k in deduped) {
    rtn.push(deduped[k]);
  }
  return rtn;
}

// Queries for elements in the DOM.
// Does not filter anything out. Simply computes the VisibilityInfo for each.
function gather(
  wnd: Window,
  doc: Document,
  renderer: Renderer,
  toLookFor: ClickStats[],
  allowHighlightRelatedElements: boolean,
  focusSelectors: string[],
  groupByProperty: GroupByPropertyDisplay | undefined,
): ClickStats[] {
  let shadowRoots: SelectorPart[][];

  // if applicable, do a first pass to gather all of the focus elements and property values for the applicable configuration
  const focusPaths = computeFocusPaths(focusSelectors, renderer);
  const propertyValuesConfig = computePropertyValuesConfig(focusPaths, groupByProperty, renderer);

  // then look for matching elements for the selectors and optional property values
  toLookFor.forEach(elemToTest => {
    const elems = queryElementsBySelector(
      elemToTest.selector(),
      elemToTest.propertyValue(),
      focusPaths,
      propertyValuesConfig,
      doc,
      renderer,
    );
    if (elems && elems.length > 0) {
      elemToTest.bindTo(wnd, elems, allowHighlightRelatedElements);
    } else {
      // could not find the element, check to see if we are dealing with ShadowDOM
      let elementSelectorChain: SelectorChain | undefined = undefined;
      try {
        elementSelectorChain = parseSelector(elemToTest.selector());
      } catch (e) {
        console.error(`invalid selector found checking Shadow DOM for: ${elemToTest.selector()}`);
        return;
      }
      const elementSelectorParts = ToSelectorParts(elementSelectorChain);
      // lazy load any shadowRoots from the document
      if (!shadowRoots) shadowRoots = gatherShadowRoots(doc);
      // loop over the shadowRoots and see if this element is in the shadow
      for (const shadowRootSelectorParts of shadowRoots) {
        // does the element match
        const match = matchesShadowRoot(shadowRootSelectorParts, elementSelectorParts);
        if (match) {
          elemToTest.setInTheShadow(true);
          break;
        }
      }
    }
  });

  return toLookFor;
}

export function gatherShadowRoots(doc: Document): SelectorPart[][] {
  if (!doc) {
    return [];
  }
  const shadowRoots = new Set<SelectorPart[]>();
  const childDocs = gatherChildDocs(doc);
  for (const aDoc of [doc, ...childDocs]) {
    const allElements = aDoc.getElementsByTagName('*');
    for (const element of allElements) {
      if (element.shadowRoot) {
        const shadowSelector = ToSelectorParts(SelectorForElem(element as HTMLElement));
        shadowRoots.add(shadowSelector);
      }
    }
  }
  return Array.from(shadowRoots);
}

// This function does a very specific matching to determine if element may be a child of a Shadow Root.
// Given that attributes between the current DOM and indexed selectors could be slightly different, we match only on
// tag names and ids (if present for both). Otherwise we greedily assume that ShadowDOM is present and there may be a match.
function matchesShadowRoot(shadowRootParts: SelectorPart[], elementParts: SelectorPart[]): boolean {
  if (!shadowRootParts || shadowRootParts.length === 0) {
    return false;
  }

  if (!elementParts || elementParts.length === 0) {
    return false;
  }

  // Remove the 'body' tag from indexed values. This will not be part of ShadowDOM roots.
  const filteredElementParts = elementParts.filter(v => v.tag.toLowerCase() !== 'body');

  for (let i = 0; i < shadowRootParts.length; i++) {
    const shadowRootPart = shadowRootParts[i];
    const elementPart: SelectorPart | undefined = filteredElementParts[i];
    if (shadowRootPart.tag.toLowerCase() !== elementPart?.tag?.toLowerCase()) {
      return false;
    }
    if (elementPart.id && shadowRootPart.id && shadowRootPart.id !== elementPart.id) {
      return false;
    }
  }

  return true;
}

export function gatherChildDocs(doc: Document): Document[] {
  return gatherIFrames(doc)
    .map(x => x.contentDocument)
    .filter(x => x != undefined);
}

// Recursively gather all of the iframes found in the given doc
export function gatherIFrames(doc: Document): HTMLIFrameElement[] {
  if (!doc) {
    return [];
  }

  const iframes = [...doc.querySelectorAll('iframe')];
  const children: HTMLIFrameElement[] = [];

  for (const iframe of iframes) {
    children.push(...gatherIFrames(iframe.contentDocument));
  }

  return iframes.concat(children);
}

// Merge together elements that have overlapping bounding boxes.
// Elide "big boxes" that correspond to body or something.
//
// TODO(jaime): Add integration tests for box eliding/folding once we are happy with the heuristic...
function merge({
  wnd,
  topElemsArg,
  pageContext,
  pageDefId,
}: {
  wnd: Window;
  topElemsArg: ClickStats[];
  pageContext: PageContext;
  pageDefId: number;
}): {
  mergedClickElems: ClickStats[];
  filteredElementDetails: ElementFilteredDetails[];
} {
  let topElems = topElemsArg;
  const topElemsByIdent: { [key: string]: ClickStats } = {};
  topElems.forEach(e => {
    const key = e.identKey();
    if (topElemsByIdent[key]) {
      topElemsByIdent[key].mergeClicks(e.clicks);
    } else {
      topElemsByIdent[key] = e;
    }
  });

  // Remove "Big boxes".
  // TODO(jaime): We should surface clicks on these containing elements. Maybe by position. And call them out maybe as
  // misses on sub component inside them.
  const windowBounds = <ElemBounds>{
    selector(): string {
      return '-viewport-';
    },
    r(): SimpleRect {
      return ViewportRect(wnd);
    },
  };

  const windowRect = windowBounds.r();
  const windowArea = Area(windowRect);
  const largeFilteredElements: ClickStats[] = [];

  topElems = topElems.filter((a): boolean => {
    // The spirit of this filter is to avoid "Big boxes" that represent large content areas like body, div, section, etc.
    // However we don't want to exclude large rectangles that may be a large clickable Hero image or Promo image.
    // Just checking for IMG tag is not enough. In some cases this is an Anchor or other. For now, we will allow all leaf elements thru.
    if (a.isLeaf()) {
      return true;
    }

    // If something isn't in the DOM. Let it through.
    const r = a.r();
    if (!r) {
      return true;
    }

    // Remove boxes that are almost the height of the viewport.
    if (!a.isNamedElement() && r.height > BIG_RECT_SIDE_THRESH * windowRect.height) {
      largeFilteredElements.push(a);
      return false;
    }

    // Remove giant boxes by area.
    if (!a.isNamedElement() && Area(r) / windowArea >= BIG_RECT_THRESH) {
      largeFilteredElements.push(a);
      return false;
    }
    return true;
  });

  const filteredElementDetails: ElementFilteredDetails[] = largeFilteredElements.map(
    clickStats => ({
      pageArgs: {
        orgId: pageContext.orgId,
        userId: pageContext.userId as int64,
        sessionId: pageContext.sessionId as int64,
        pageId: pageContext.pageId as int64,
      },
      elementDetails: {
        selector: clickStats.selector(),
        isNamedElement: clickStats.isNamedElement(),
        namedElementId: clickStats.stats().NamedElementId,
        propertyValue: clickStats.stats().PropertyValue,
      },
      dimensions: {
        windowHeight: windowRect.height,
        elementHeight: clickStats.r().height,
      },
      pageDefId,
    }),
  );

  // Fold together elements that are perceptually "the same click target".
  // We do this via heuristics examining the areas of elements that contain each other.
  // And by looking at the "negative space" left between them.
  const folded: { [key: string]: ClickStats } = {};
  const fold = (smaller: ClickStats, largerArg: ClickStats) => {
    let larger = largerArg;
    if (folded[larger.identKey()]) {
      // If the larger thing is itself folded already. Let's go ahead and add this thing to the root of that tree.
      const root = findRootRect(folded, larger, smaller);
      if (!root) {
        // Cycle! This shouldn't happen. If it does we need to debug the RectangleIntersection sorting.
        console.log(
          `Cycle detected in element folding [${smaller.identKey()}] into [${larger.identKey()}]: `,
          smaller.r(),
          larger.r(),
        );
        for (const k in folded) {
          console.log(k, ' -> ', folded[k].identKey());
        }
        return;
      }
      larger = root;
    }

    // If the larger element has barely any clicks on it. Don't fold.
    if (
      topElemsByIdent[larger.identKey()].clicks.count <
      MERGING_CLICK_COUNT_THRESH * topElemsByIdent[smaller.identKey()].clicks.count
    ) {
      return;
    } else if (smaller.isNamedElement()) {
      // Don't fold Named Elements into other values. However, we'll allow for non Named Element
      // selectors to be folded into a Named Element.
      return;
    }

    // Proceed with folding. Smaller is consumed by the larger one.
    folded[smaller.identKey()] = larger;
    intersection.larger().mergeClicks(intersection.smaller().clicks);
  };

  // Walk all the intersections, apply the heuristics, and fold together if they pass.
  const intersections = new RectIntersections(topElems);
  let intersection = undefined;
  for (let i = 0; i < intersections.intersections.length; i++) {
    // Sorted in increasing order of area of intersection.
    intersection = intersections.intersections[i];

    // If the smaller one is already folded into something else. Skip it. Since we don't want to double count.
    // Also skip if there isn't even a containment relationship between the two rectangles.
    if (folded[intersection.smaller().identKey()] || !intersection.isContains()) {
      continue;
    }

    // Now we must compare areas to decide if we want to fold together.
    const larger = intersection.larger();
    const smaller = intersection.smaller();
    const sr = smaller.r();
    const lr = larger.r();

    //
    // Heuristic 1: Merge elements with similar area.
    if (AreasMoreSimilarThan(sr, lr, MERGING_AREA_SIMILARITY_THRESH)) {
      fold(smaller, larger);
      continue;
    }

    //
    // Heuristic 2: Merge elements that leave "small negative space".
    // For elements that are tall enough, merge them if their horizontal negative space leaves tiny
    // rectangles. So even if the overall inner area doesn't pass heuristic 1, if the inner element obscures enough contiguous space,
    // such that the negative space is small. Combine them. This is often true on mobile for full width buttons with inner labels.
    if (sr.height >= NEGATIVE_HEIGHT_THRESH * lr.height) {
      const negativeSpaceWidth = Math.max(sr.left - lr.left, lr.right - sr.right);
      const negativeSpace = <SimpleRect>{
        top: 0,
        left: 0,
        right: negativeSpaceWidth,
        bottom: lr.height,
        height: lr.height,
        width: negativeSpaceWidth,
      };
      if (AreaRatio(negativeSpace, lr) < NEGATIVE_AREA_SIMILARITY_THRESH) {
        fold(smaller, larger);
      }
    }
  }

  // Remove any rect that was folded into another.
  return {
    mergedClickElems: topElems.filter((a): boolean => !folded[a.identKey()]),
    filteredElementDetails,
  };
}

function findRootRect(
  folded: { [key: string]: ClickStats },
  largerArg,
  smaller: ClickStats,
): ClickStats {
  let larger = largerArg;
  const seenSoFar: { [key: string]: boolean } = {}; // for cycle detection.
  seenSoFar[larger.identKey()] = true;
  seenSoFar[smaller.identKey()] = true;
  while (folded[larger.identKey()]) {
    larger = folded[larger.identKey()];
    if (seenSoFar[larger.identKey()]) {
      // Cycle!
      return null;
    }
  }
  return larger;
}

// Element Visibility
function extractVisibilityInfo(wnd: Window, e: Q.OfOne): VisibilityInfo {
  const rect = e.bounds();
  const couldClick = couldBeClicked(e, rect);
  return <VisibilityInfo>{
    ExistsInDom: true,
    IsInViewport: couldClick ? isInViewport(wnd, rect) : false,
    CouldBeClicked: couldClick,
    IsOnTop: couldClick ? isOnTop(e, rect) : false,
  };
}

function couldBeClicked(elem: Q.OfOne, rect: SimpleRect): boolean {
  return rect.width > 0 && rect.height > 0 && elem.css('visibility') != 'hidden';
}

function isInViewport(wnd: Window, rect: SimpleRect): boolean {
  const intersection = ClientRectIntersection(ViewportRect(wnd), rect);
  return intersection.width > 0 && intersection.height > 0;
}

function isOnTop(elem: Q.OfOne, rect: SimpleRect): boolean {
  const centroidX = rect.left + rect.width / 2;
  const centroidY = rect.top + rect.top / 2;
  const elemAt = elem.e.ownerDocument.elementFromPoint(centroidX, centroidY);
  return elemAt == elem.e;
}

/**
 * @returns information about elements in the provided frame, or any descendant
 * frame, that intersect the requested point. The results are ordered according
 * to visual depth; the first element in the array is visually on top, closest
 * to the viewer, and subsequent elements are on progressively deeper layers.
 */
export function queryElementInfoByPoint(
  point: Point,
  frame: HTMLIFrameElement,
  parentTransform?: MatrixAndInverse,
): ElementInfoQueryMatch[] {
  if (!point) return [];
  if (!frame) return [];

  const innerDoc = frame.contentDocument;
  if (!innerDoc) return [];

  const transform = updateTransformForFrame(frame, parentTransform);
  const pointWithinFrame = transform.matrix.transformPoint(point);

  const elements = queryPointFor(innerDoc, pointWithinFrame) ?? [];
  return flatMap(elements, e => {
    switch (e.tagName.toLowerCase()) {
      case 'iframe':
        return [
          ...queryElementInfoByPoint(point, e as HTMLIFrameElement, transform),
          makeElementInfo(e, transform),
        ];

      case 'html':
      case 'body':
        return [];

      default:
        return [makeElementInfo(e, transform)];
    }
  });
}

/**
 * @returns information about elements in the provided frame, or any descendant
 * frame, that match the requested selector.
 */
export function queryElementInfoBySelector(
  selector: string,
  frame: HTMLIFrameElement,
  parentTransform?: MatrixAndInverse,
): ElementInfoQueryMatch[] {
  if (!frame) return [];

  const innerDoc = frame.contentDocument;
  if (!innerDoc) return [];

  const transform = updateTransformForFrame(frame, parentTransform);

  const elementList = queryElemsFor(innerDoc, selector);
  const elements = elementList ? [...elementList] : [];
  const elementResults = elements.map(e => makeElementInfo(e, transform));

  const childFrames = [...innerDoc.querySelectorAll('iframe')];
  const childFrameResults = flatMap(childFrames, frame =>
    queryElementInfoBySelector(selector, frame as HTMLIFrameElement, transform),
  );

  return [...childFrameResults, ...elementResults];
}

function makeElementInfo(e: Element, transform: MatrixAndInverse): ElementInfoQueryMatch {
  return {
    selector: SelectorForElem(e as HTMLElement),
    rect: transformRect2D(transform.inverse, e.getBoundingClientRect()),
  };
}

function transformRect2D(m: Matrix, r: DOMRect): DOMRect {
  const topLeft = m.transformPoint({ x: r.left, y: r.top });
  const bottomRight = m.transformPoint({ x: r.right, y: r.bottom });
  return DOMRect.fromRect({
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
  });
}

type MatrixAndInverse = {
  matrix: Matrix;
  inverse: Matrix;
};

function updateTransformForFrame(
  frame: HTMLIFrameElement,
  parentTransform: MatrixAndInverse = { matrix: new Matrix(), inverse: new Matrix() },
): MatrixAndInverse {
  const innerDoc = frame.contentDocument;
  const innerDocElem = innerDoc?.documentElement;
  const innerWindow = frame.contentWindow;
  if (!innerDoc || !innerDocElem || !innerWindow) {
    return parentTransform;
  }

  // Compute the bounds of the viewport inside the frame relative to the frame's
  // inner document. getBoundingClientRect() mostly does what we need here, but
  // it can return a rect smaller than the viewport if the document doesn't
  // contain much content, so we also need to check innerWidth/Height.
  const innerDocBounds = innerDocElem.getBoundingClientRect();
  const innerViewportBounds = DOMRect.fromRect({
    x: innerDocBounds.x,
    y: innerDocBounds.y,
    width: Math.max(innerDocBounds.width, innerWindow.innerWidth),
    height: Math.max(innerDocBounds.height, innerWindow.innerHeight),
  });
  if (innerViewportBounds.width === 0 || innerViewportBounds.height === 0) {
    // The viewport has zero size; there's no useful way to map a point from the
    // frame's owner document into it.
    return parentTransform;
  }

  // Compute the bounds of the content area of the frame; this is the area in
  // the outer document corresponding to the viewport inside the frame.
  const frameContentBoxBounds = iframeContentArea(frame);
  if (
    frameContentBoxBounds.width === 0 ||
    frameContentBoxBounds.height === 0 ||
    isNaN(frameContentBoxBounds.width) ||
    isNaN(frameContentBoxBounds.height) ||
    isNaN(frameContentBoxBounds.x) ||
    isNaN(frameContentBoxBounds.y)
  ) {
    // The frame's content area has zero size; there's no useful way to map a
    // point from the frame's owner document into the inner document.
    return parentTransform;
  }

  const scale = innerViewportBounds.width / frameContentBoxBounds.width;
  if (frameContentBoxBounds.x === 0 && frameContentBoxBounds.y === 0 && scale === 1) {
    // The new transform would be identical to the old one; just reuse it.
    return parentTransform;
  }

  // We now know how to transform a point in the outer frame's owner document to
  // the equivalent point in the inner document. We can calculate this transform
  // because we know the bounds of the inner document's viewport in both
  // coordinate systems: innerViewportBounds gives us the bounds from the inner
  // document's perspective, and frameContentBoxBounds gives us the bounds from
  // the perspective of the outer frame's owner document. We can combine this
  // information with the transform we've already accumulated from each ancestor
  // document to determine the overall transform from the original coordinate
  // system of the query to this new inner document's coordinate system.

  // First, we'll create a new transform that combines the transform we've
  // accumulated from our ancestor documents with the translation and scale
  // needed to enter the coordinate system of the new inner document. This
  // transform can be applied to the query point to give us the equivalent point
  // in the inner document.
  const matrix = parentTransform.matrix.scale(scale);
  matrix.translateSelf(-frameContentBoxBounds.x, -frameContentBoxBounds.y);

  // Second, we'll calculate the inverse of the new transform. This inverse
  // transform can be applied to points in the new inner document to give us the
  // equivalent point in the original coordinate system of the query.
  const inverse = matrix.inverse();

  return { matrix, inverse };
}

/**
 * @returns elements from the provided document, or any descendant document,
 * that match the requested selector.
 */
export function queryElementsBySelector(
  selector: string,
  propertyValue: string | undefined,
  focusPaths: Set<string> | undefined,
  propertyValuesConfig: PropertyValuesConfig | undefined,
  document: Document,
  renderer: Renderer | null,
  ancestry: HTMLIFrameElement[] = [],
): ElementQueryMatch[] {
  if (!document) {
    return [];
  }

  const elements = [];
  queryElemsFor(document, selector)?.forEach(e => {
    if (
      satisfiesConstraintsForElement(propertyValue, e, focusPaths, propertyValuesConfig, renderer)
    ) {
      elements.push(e);
    }
  });

  const match: ElementQueryMatch = {
    frameAncestry: ancestry,
    elements: new Q.OfMany(elements),
  };

  const children = [...document.querySelectorAll('iframe')];

  const childMatches = flatMap(children, (iframe: HTMLIFrameElement) => {
    if (!iframe.contentDocument) {
      return [];
    }
    return queryElementsBySelector(
      selector,
      propertyValue,
      focusPaths,
      propertyValuesConfig,
      iframe.contentDocument,
      renderer,
      [iframe, ...ancestry],
    );
  });

  if (match.elements.empty()) {
    return [...childMatches];
  } else {
    return [match, ...childMatches];
  }
}

function satisfiesConstraintsForElement(
  rawPropertyValue: string | undefined,
  e: Element,
  focusPaths: Set<string> | undefined,
  propertyValuesConfig: PropertyValuesConfig | undefined,
  renderer: Renderer,
): boolean {
  const node = (e as RenderedNode)._fs;
  const path = node?.path?.toString() ?? '';
  return satisfiesConstraints(rawPropertyValue, path, focusPaths, propertyValuesConfig, renderer);
}

function queryPointFor(doc: Document, pt: Point): Element[] | null {
  if (!pt) return null;
  if (!doc) return null;

  try {
    return doc.elementsFromPoint(pt.x, pt.y);
  } catch (e) {
    console.log(`Failed to query for: { x: ${pt.x}, y: ${pt.y} }`);
    return null;
  }
}

export function queryElemsFor(doc: Document, sel: string): NodeListOf<Element> | null {
  if (!sel) {
    return null;
  }

  try {
    return doc.querySelectorAll(sel);
  } catch (e) {
    console.log(`Failed to query for: '${sel}'`);
    return null;
  }
}

/**
 * @returns the content area of the given iframe.
 */
function iframeContentArea(frame: HTMLIFrameElement): DOMRect {
  if (!frame || !frame.ownerDocument || !frame.ownerDocument.defaultView) {
    return new DOMRect();
  }

  // This calculation is a bit more awkward than it should be, because
  // getBoundingClientRect() includes the border and padding of the iframe; we
  // need to remove them to get *only* the content area. This area corresponds
  // to the viewport inside of the frame.
  const frameBorderBoxBounds = frame.getBoundingClientRect();
  const frameStyle = frame.ownerDocument.defaultView.getComputedStyle(frame);
  const insetLeft = parseFloat(frameStyle.borderLeftWidth) + parseFloat(frameStyle.paddingLeft);
  const insetRight = parseFloat(frameStyle.borderRightWidth) + parseFloat(frameStyle.paddingRight);
  const insetTop = parseFloat(frameStyle.borderTopWidth) + parseFloat(frameStyle.paddingTop);
  const insetBottom =
    parseFloat(frameStyle.borderBottomWidth) + parseFloat(frameStyle.paddingBottom);

  return DOMRect.fromRect({
    x: frameBorderBoxBounds.x + insetLeft,
    y: frameBorderBoxBounds.y + insetTop,
    width: frameBorderBoxBounds.width - insetLeft - insetRight,
    height: frameBorderBoxBounds.height - insetTop - insetBottom,
  });
}
